import { createTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { currentPortalDefaultTheme, getThemeComponents } from '../Theme';
import { deepmerge } from '@mui/utils';
import type { BrandThemeOptions } from './themeTypes';

// factory function to create brand themes

export const createBrandTheme = (brandOptions: BrandThemeOptions): Theme => {
	// The brand options must be merged into the defaults *before* createTheme runs.
	// createTheme only passes its first argument through createPalette/augmentColor;
	// later positional arguments are a plain deepmerge applied afterwards, which
	// would leave a brand's primary.light/dark/contrastText derived from the
	// default palette rather than from the brand's own main colour.
	const themeWithDefaults = createTheme(
		deepmerge(currentPortalDefaultTheme, brandOptions),
	);

	const dynamicComponents = getThemeComponents(themeWithDefaults);

	const theme = createTheme(themeWithDefaults, {
		components: deepmerge(dynamicComponents, brandOptions.components || {}),
	});

	assertThemeContract(theme);

	return theme;
};

// `Palette.severity` and `Theme.app` are required to read but optional to
// write, so TypeScript believes they are always present while a theme built
// outside this factory would leave them undefined. Around thirty components
// dereference them unguarded, so fail loudly here rather than at first render.
function assertThemeContract(theme: Theme) {
	const missing: string[] = [];

	if (!theme.palette.severity) missing.push('palette.severity');
	if (!theme.app) missing.push('app');
	else {
		if (!theme.app.ai) missing.push('app.ai');
		if (!theme.app.avatarColors?.length) missing.push('app.avatarColors');
		if (!theme.app.layout) missing.push('app.layout');
		else {
			// Checked per-token, unlike the sibling groups above, because both are
			// dereferenced on every render and neither fails loudly on its own:
			// breakpoints.down(undefined) does not throw, it returns a malformed
			// query that silently resolves to "always desktop" at every viewport.
			if (!theme.app.layout.mobileBreakpoint)
				missing.push('app.layout.mobileBreakpoint');
			if (!theme.app.layout.pageGutter)
				missing.push('app.layout.pageGutter');
		}
		if (!theme.app.navigation) missing.push('app.navigation');
		if (!theme.app.surface) missing.push('app.surface');
		if (!theme.app.shape) missing.push('app.shape');
		if (!theme.app.typography) missing.push('app.typography');
	}

	if (missing.length > 0) {
		throw new Error(
			`Brand theme is missing required tokens: ${missing.join(', ')}.`,
		);
	}
}
